import { durations, visibility } from '@betgames/bg-tools';
import {
    partnerSettingsStore,
    t,
    gamesStatusService,
    webSocketsRepository,
    promotions,
    balanceService,
    sessionStore,
    iframeIntegration,
    reportsApi,
    authService,
    authStore,
    recentBetsEvents,
    recentBetsStore,
    dateFnsLocale,
    DurationUnits,
    MAX_RECENT_BETS,
    configureTax,
    loadResizeObserverPolyfill,
    assetsService,
    GameId,
    ClassicGameDefinition,
    balanceStore,
    IStagedBetEvent,
    STAGED_BETS_RESPONSE,
    stagedBetsStore,
    gamificationService,
} from '@bg-shared';
import { StaticServer } from '@bg-shared/utils/StaticServer';

export default class AppKernelService {
    private initPartnerCustomLogic(): void {
        if (_featuresConfig.disableContextMenu) {
            document.addEventListener(
                'contextmenu',
                (e) => {
                    e.preventDefault();
                },
                false,
            );
        }
    }

    public async configure(): Promise<void> {
        iframeIntegration.init();
        document.documentElement.setAttribute('data-language', sessionStore.language);

        await authService.validate();

        const { enabledInHouseGames, partnerSettings } = partnerSettingsStore;

        const cashOutOnDisconnectThreshold = ClassicGameDefinition.SKYWARD_GAMES_IDS.reduce(
            (acc, item) => {
                acc[item] = partnerSettingsStore.getCashOutOnDisconnectThreshold(item);
                return acc;
            },
            {} as Record<GameId, number | null>,
        );

        webSocketsRepository.configure(
            {
                cashOutOnDisconnectThreshold,
                url: sessionStore.webSocketsUrl || partnerSettings.webSocketUrl,
                token: sessionStore.webSocketsToken,
                partnerCode: sessionStore.partnerCode,
                playerId: sessionStore.playerId || null,
            },
            () => {
                if (authStore.value && !visibility.isHidden()) {
                    reportsApi.fetchRecentBets(MAX_RECENT_BETS).then((bets) => {
                        recentBetsStore.saveBetsList(bets);
                    });
                }
            },
        );

        await Promise.all([
            t.init(sessionStore.language, sessionStore.partnerCode),
            dateFnsLocale.setup(sessionStore.language),
            assetsService.init({
                rtl: partnerSettings.isRtl,
                cssCustom: partnerSettings.cssCustom,
                scrolling: sessionStore.scrolling,
                isLithuaniaJurisdiction: partnerSettingsStore.isLithuaniaJurisdiction,
            }),
            sessionStore.session.themeEditor &&
                iframeIntegration.initThemeEditor(assetsService.generateCssVarModifications),
            loadResizeObserverPolyfill(),
            StaticServer.configure(),
        ]);

        if (authStore.value) {
            authService.refreshTokenEvent(partnerSettings.refreshTokenFrequency);

            gamificationService.configure();

            if (partnerSettingsStore.showBalance) {
                balanceService.runEvents();
            }

            reportsApi.fetchRecentBets(MAX_RECENT_BETS).then((bets) => {
                recentBetsStore.saveBetsList(bets);
                recentBetsEvents.configure();
            });
        }

        durations.setup([
            t.dates(DurationUnits.YEARS),
            t.dates(DurationUnits.MONTHS),
            t.dates(DurationUnits.WEEKS),
            t.dates(DurationUnits.DAYS),
            t.dates(DurationUnits.HOURS),
            t.dates(DurationUnits.MINUTES),
            t.dates(DurationUnits.SECONDS),
        ]);

        gamesStatusService.configureStates(
            enabledInHouseGames.map((game) => ({
                gameId: game.gameId,
                state: game.state,
                delay: game.streamDelay,
            })),
        );

        configureTax();

        if (partnerSettings.isNotificationEnabled) {
            promotions.store.init();
        }

        if (
            partnerSettingsStore.isUnitedKingdomMarket ||
            partnerSettingsStore.isRealtimeBalanceEnabled
        ) {
            webSocketsRepository.on<IStagedBetEvent>(STAGED_BETS_RESPONSE, (event) => {
                balanceStore.updateStagedBalance(event.gameId, 0, event.balance);
                stagedBetsStore.reset();
            });
        }

        if (partnerSettingsStore.isRealtimeBalanceEnabled) {
            balanceService.getTotalStagedBetsAmount().then((data) => {
                data.forEach((item) => {
                    balanceStore.updateStagedBalance(
                        item.gameId,
                        item.stagedBets.bets.reduce((acc, bet) => acc + bet.amount, 0),
                    );
                });
            });
        }

        if (partnerSettingsStore.isUnitedKingdomMarket) {
            stagedBetsStore.updateFromAPI();
        }

        this.initPartnerCustomLogic();
    }
}

export const appKernel = new AppKernelService();
